import { Emitter } from "./Emitter.js";
import { ZxingEngine } from "./engine/ZxingEngine.js";
import { CameraController } from "./camera/CameraController.js";
import { ScanPipeline } from "./pipeline/ScanPipeline.js";
import { decodeWithEnhancement } from "./pipeline/decodeWithEnhancement.js";
import { BeepPlayer } from "./audio/beep.js";
import { ScannerView } from "./ui/ScannerView.js";
import { SettingsPanel } from "./ui/SettingsPanel.js";
import { DEFAULT_SETTINGS, type ScanResult, type ScanSettings, type ScannerEventMap } from "./types.js";
import { SUPPORTED_FORMAT_IDS } from "./formats.js";

export interface BarcodeScannerOptions {
  settings?: Partial<ScanSettings>;
  /** Override where zxing-wasm fetches its .wasm binary from (defaults to jsDelivr CDN). */
  wasmLocateFile?: (path: string, prefix: string) => string;
  /** Max frames per second the decode pipeline processes (rendering itself is unaffected). */
  targetFps?: number;
}

/**
 * Public entry point of the SDK. Owns the camera, the decode pipeline and
 * the built-in UI (scanner view + settings panel), and re-exposes
 * everything through a small typed event API so host apps don't need to
 * touch zxing-wasm, MediaStream, or canvas plumbing directly.
 */
export class BarcodeScanner {
  private readonly emitter = new Emitter<ScannerEventMap>();
  private readonly _engine: ZxingEngine;
  private readonly beep = new BeepPlayer();
  private readonly options: BarcodeScannerOptions;
  // camera/pipeline are created in mount(), not here: the camera has to be
  // pointed at the *visible* <video> element inside ScannerView's shadow
  // DOM, which doesn't exist until mount() creates it. Building them early
  // against a detached video element was a real bug — the stream played
  // into a video nobody ever saw, while ScannerView showed its own,
  // never-attached video (solid black, viewfinder UI on top of it working
  // fine, decode still running invisibly).
  private camera: CameraController | null = null;
  private pipeline: ScanPipeline | null = null;
  private view: ScannerView | null = null;
  private settingsPanel: SettingsPanel | null = null;
  private facingMode: "environment" | "user" = "environment";
  private settings: ScanSettings;

  constructor(options: BarcodeScannerOptions = {}) {
    this.options = options;
    this.settings = {
      ...DEFAULT_SETTINGS,
      enabledFormats: SUPPORTED_FORMAT_IDS,
      ...options.settings,
    };
    this._engine = new ZxingEngine({ wasmLocateFile: options.wasmLocateFile });
    this._engine.warmUp();
  }

  on<K extends keyof ScannerEventMap>(event: K, cb: (payload: ScannerEventMap[K]) => void): () => void {
    return this.emitter.on(event, cb);
  }

  /** Direct access to the underlying decode engine, e.g. to compare a raw vs. enhanced decode attempt. */
  get engine(): ZxingEngine {
    return this._engine;
  }

  /** Mount the built-in scanner UI (video + viewfinder + AR overlay) into a container element. */
  mount(container: HTMLElement): ScannerView {
    container.innerHTML = "";
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "position:relative;width:100%;height:100%;";

    this.view = new ScannerView();
    this.view.style.cssText = "position:absolute;inset:0;";
    this.applySettingsToView();

    this.camera?.stop();
    this.camera = new CameraController(this.view.videoEl);
    this.pipeline = new ScanPipeline({
      engine: this._engine,
      video: this.camera.video,
      getSettings: () => this.settings,
      getRoi: () => this.view?.computeRoi() ?? null,
      onResults: (frame, fresh, batch) => this.handleResults(frame, fresh, batch),
      onFrameQuality: (q) => {
        this.view?.setFrameQuality(q);
        this.emitter.emit("frameQuality", q);
      },
      onTorchRequest: (on) => this.applyTorch(on),
      onError: (err) => this.emitter.emit("error", err),
      targetFps: this.options.targetFps,
    });

    this.settingsPanel = new SettingsPanel();
    this.settingsPanel.style.cssText = "position:absolute;inset:0;display:none;";
    this.settingsPanel.settings = this.settings;

    this.view.addEventListener("home", () => this.emitter.emit("home", undefined));
    this.view.addEventListener("settings", () => this.openSettingsPanel());
    this.view.addEventListener("switch-camera", () => this.toggleCameraPicker());
    this.view.addEventListener("select-camera", (e) => {
      this.selectCamera((e as CustomEvent<string>).detail);
    });
    this.view.addEventListener("torch", () => this.toggleTorch());
    this.view.addEventListener("file-upload", (e) => {
      const file = (e as CustomEvent<File>).detail;
      this.scanImage(file).then((results) => {
        if (results[0]) {
          this.view?.showResultToast(results[0]);
          this.emitter.emit("scan", results[0]);
        } else {
          this.view?.showMessage("No barcode found in image");
        }
      });
    });

    this.settingsPanel.addEventListener("back", () => this.closeSettingsPanel());
    this.settingsPanel.addEventListener("settings-change", (e) => {
      this.updateSettings((e as CustomEvent<Partial<ScanSettings>>).detail);
    });

    wrapper.append(this.view, this.settingsPanel);
    container.append(wrapper);
    return this.view;
  }

  openSettingsPanel(): void {
    if (!this.view || !this.settingsPanel) return;
    this.view.hideCameraPicker();
    this.settingsPanel.style.display = "block";
    this.emitter.emit("settingsOpen", undefined);
  }

  closeSettingsPanel(): void {
    if (!this.settingsPanel) return;
    this.settingsPanel.style.display = "none";
    this.emitter.emit("settingsClose", undefined);
  }

  async start(): Promise<void> {
    if (!this.view || !this.camera) throw new Error("Call mount(container) before start().");
    await this.camera.start({ facingMode: this.facingMode });
    this.view.setTorchAvailable(this.camera.supportsTorch);
    this.pipeline?.start();
    this.emitter.emit("start", undefined);
  }

  stop(): void {
    this.pipeline?.stop();
    this.camera?.stop();
    this.view?.clearOverlay();
    this.view?.hideResultToast();
    this.emitter.emit("stop", undefined);
  }

  /** Resume scanning after a single-shot decode (multipleScanning: false) without restarting the camera. */
  resume(): void {
    this.view?.hideResultToast();
    this.pipeline?.start();
  }

  destroy(): void {
    this.stop();
    this.emitter.clear();
    this.view = null;
    this.settingsPanel = null;
    this.camera = null;
    this.pipeline = null;
  }

  getSettings(): ScanSettings {
    return { ...this.settings };
  }

  updateSettings(patch: Partial<ScanSettings>): void {
    this.settings = { ...this.settings, ...patch };
    this.applySettingsToView();
    if (this.settingsPanel) this.settingsPanel.settings = this.settings;
    this.emitter.emit("settingsChange", this.getSettings());
  }

  setEnabledFormats(ids: string[]): void {
    this.updateSettings({ enabledFormats: ids });
  }

  private applySettingsToView(): void {
    if (!this.view) return;
    this.view.setViewFinderVisible(this.settings.viewFinder);
    if (!this.settings.arOverlay) this.view.clearOverlay();
  }

  /** Simple front/back toggle, for consumers building their own camera-switch UI instead of the built-in device picker. */
  async switchCamera(): Promise<void> {
    if (!this.camera) return;
    this.facingMode = this.facingMode === "environment" ? "user" : "environment";
    this.camera.stop();
    this.pipeline?.stop();
    await this.camera.start({ facingMode: this.facingMode });
    this.view?.setTorchAvailable(this.camera.supportsTorch);
    this.pipeline?.start();
  }

  /** List every camera the browser currently exposes (labels are only populated once permission has been granted). */
  async listCameras(): Promise<MediaDeviceInfo[]> {
    return (await this.camera?.listVideoInputs()) ?? [];
  }

  /** Switch the live stream to a specific device, e.g. picked from `listCameras()`. */
  async selectCamera(deviceId: string): Promise<void> {
    if (!this.camera) return;
    this.pipeline?.stop();
    await this.camera.switchDevice(deviceId);
    this.view?.setTorchAvailable(this.camera.supportsTorch);
    this.pipeline?.start();
  }

  private async toggleCameraPicker(): Promise<void> {
    if (!this.view || !this.camera) return;
    if (this.view.isCameraPickerOpen()) {
      this.view.hideCameraPicker();
      return;
    }
    // The picker and the top-center notice occupy the same row; only one
    // floating panel should be up at a time so neither one visually covers
    // the other (they're independent absolute-positioned elements, so
    // nothing else would ever close one when the other opens).
    this.view.hideTopNotice();
    const devices = await this.listCameras();
    this.view.showCameraPicker(
      devices.map((d) => ({ deviceId: d.deviceId, label: d.label })),
      this.camera.activeDeviceId,
    );
  }

  private async toggleTorch(): Promise<void> {
    this.view?.hideCameraPicker();
    if (!this.camera?.supportsTorch) {
      this.view?.showTopNotice("Flashlight not available on this device");
      return;
    }
    const next = this.settings.torch === "on" ? "off" : "on";
    const ok = await this.applyTorch(next === "on");
    if (ok) {
      this.updateSettings({ torch: next });
    } else {
      this.view?.showTopNotice("Flashlight not available on this device");
    }
  }

  /** Low-level torch apply shared by the manual toggle and the pipeline's auto-torch trigger. */
  private async applyTorch(on: boolean): Promise<boolean> {
    const ok = (await this.camera?.setTorch(on)) ?? false;
    if (ok) {
      this.view?.setTorchState(on);
      this.emitter.emit("torch", on);
    }
    return ok;
  }

  /**
   * One-off decode from a file/blob/ImageData, independent of the live
   * camera pipeline (e.g. an "upload a photo" fallback, or headless/test
   * usage). Goes through the same low-light/glare enhancement retry as the
   * live pipeline, so a bad photo benefits from it too.
   */
  async scanImage(source: Blob | ImageData): Promise<ScanResult[]> {
    const imageData = source instanceof ImageData ? source : await blobToImageData(source);
    const { results, enhanced } = await decodeWithEnhancement(
      this._engine,
      imageData,
      this.settings.enabledFormats,
      this.settings.enhancement,
    );
    const timestamp = performance.now();
    return results.map((r) => ({ ...r, enhanced, timestamp }));
  }

  private handleResults(frame: ScanResult[], fresh: ScanResult[], batch: ScanResult[]): void {
    if (this.settings.arOverlay) this.view?.drawOverlay(frame);
    for (const result of fresh) {
      this.emitter.emit("scan", result);
      if (this.settings.beepTone) this.beep.play();
    }
    if (this.settings.multipleScanning) {
      this.view?.setBatchCount(batch.length);
      if (fresh[0]) this.view?.showResultToast(fresh[0]);
      if (fresh.length > 0) this.emitter.emit("scanBatch", batch);
    } else if (frame[0]) {
      this.view?.showResultToast(frame[0], 0);
      this.emitter.emit("scanBatch", batch);
    }
  }
}

async function blobToImageData(blob: Blob): Promise<ImageData> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}
